<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { isUndefined, omitBy } from "lodash-es";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import VStack from "@magnit/core/src/components/VStack/VStack.vue";
import VStackItem from "@magnit/core/src/components/VStack/VStackItem.vue";
import VYaSlot from "@magnit/core/src/containers/VYaSlot/VYaSlot.vue";
import VCatalogListing from "@magnit/unit-catalog/src/components/VCatalogListing.vue";
import VCatalogSorting from "@magnit/unit-catalog/src/components/VCatalogSorting.vue";
import VCatalogFiltersButton from "@magnit/unit-filter/src/components/Button.vue";
import VCatalogFilters from "@magnit/unit-filter/src/components/VFilters.vue";
import VCatalogFiltersModal from "@magnit/unit-filter/src/components/VFiltersModal.vue";
import type {
  IProduct,
  ISortingValue,
  ISortOrder,
} from "@magnit/unit-catalog/src/types";
import type { IPaginationProps } from "@magnit/core/src/containers/VPagination/VPagination.types";
import VBreadcrumbsWrapped from "~/components/VBreadcrumbs/VBreadcrumbsWrapped.vue";
import type { IBreadcrumbsItemProps } from "~/components/VBreadcrumbs/VBreadcrumbs.types";
import VPageBack from "~/components/VPageBack.vue";
import VWrapperLocal from "~/components/VWrapperLocal.vue";
import VCatalogHeaderSlider from "~/containers/VCatalog/VCatalogHeaderSlider.vue";
import VCatalogHeader from "~/containers/VCatalog/VCatalogHeader.vue";
import VCatalogBottomSlot from "~/containers/VCatalog/VCatalogBottomSlot.vue";
import { Routes } from "~/utils/routes";
import { scrollToElement } from "~/utils/scrollToElement";
import useCatalogYaSlotConfig from "~/composables/useCatalogYaSlotConfig";
import type { IRequestPromotionsParams } from "~/typings/api/promo";
import { collectCatalogListingPayload } from "~/utils/analytics";
import { IYaSlotApiStatus, useSlotsStore } from "~/store/useSlotsStore";
import type { ICategory } from "~/typings/categories";

const route = useRoute();
const router = useRouter();
const { yaSlotConfig } = useCatalogYaSlotConfig();
const { slotStatus } = storeToRefs(useSlotsStore());
const { isError } = useCategoryError("promo");
const { send } = useAnalytics();
const { useCatalogFiltersModal } = useModals();
const filtersModal = useCatalogFiltersModal();
const { requestFilters } = goodsApi();
const { requestPromotions } = promoApi();
const { requestShopCode } = storeToRefs(useStoresStore());
const categoriesStore = useCategoriesStore();
const contentStore = useContentStore();

const settingsRef = ref<HTMLElement>();

const appendProducts = ref(false);
const products = ref<IProduct[]>([]);
let pagination = reactive<IPaginationProps>({});
const sortingModel = ref<ISortingValue>({
  value: (route.query?.sortBy as string) || DEFAULT_SORT_VALUE_PROMO,
  order: (route.query?.order as ISortOrder) || DEFAULT_SORT_ORDER,
});

if (isError.value) {
  throw createError({
    statusCode: HttpCodes.NotFound,
    message: `invalid "${isError.value}" route params`,
    fatal: true,
  });
}

const sortBy = computed(() =>
  route.query.sortBy ? String(route.query.sortBy) : undefined,
);
const order = computed(() =>
  route.query.order ? String(route.query.order) : undefined,
);
const page = computed(() => (route.query.page ? Number(route.query.page) : 1));
const analyticsCatalogListingPayload = computed(() =>
  collectCatalogListingPayload(
    sortBy.value ? sortingModel.value : null,
    innerFilters.value,
    currentCategory.value,
  ),
);

const yaSlotAnalyticsView = computed(() => ({
  name: "CatalogListing:AdFoxGoods:View",
}));

const yaSlotAnalyticsClick = computed(() => ({
  name: "CatalogListing:AdFoxGoods:Click",
}));

const {
  countChangeFilters,
  innerFilters,
  defaultFilters,
  currentCategory,
  onResetFilters,
  getFiltersPayload,
  getArrayFilters,
} = useCatalogFilters("promo");

const payload = computed(() => {
  const limit = PRODUCTS_LIMIT - PRODUCTS_BANNER_LIMIT_OFFSET;
  const params: IRequestPromotionsParams = {
    categoryId: currentCategory.value?.id,
    offset: (page.value - 1) * limit,
    limit,
    storeCode: requestShopCode.value,
    adult: true,
    sortBy: sortBy.value,
    order: order.value,
  };
  return omitBy(params, isUndefined);
});
const yaSlotSuccess = computed(
  () => slotStatus.value === IYaSlotApiStatus.SUCCESS,
);
const yaSlotError = computed(() => slotStatus.value === IYaSlotApiStatus.ERROR);

const breadcrumbs = computed<IBreadcrumbsItemProps[]>(() => {
  let steps: ICategory[] = [];
  if (currentCategory.value && categoriesStore.promo.length) {
    steps = categoriesStore.getParentsByKey(currentCategory.value.key);
    steps.push(currentCategory.value);
  }
  return [
    {
      to: Routes.Main,
      title: "Главная",
    },
    ...steps.map((step, index) => {
      const obj: IBreadcrumbsItemProps = {
        title: step.name,
      };

      if (index !== steps.length - 1) {
        obj.to = step.url;
      }

      return obj;
    }),
  ];
});

const onBackClick = () => {
  send("PromoCatalog:Back:Button:Click", {
    categoryName: currentCategory.value
      ? categoriesStore.getTreeNameByKey(currentCategory.value.key)
      : "",
  });
};

const { pending, data } = await requestPromotions(payload);
await contentStore.requestCatalog();

const handleShowMore = () => {
  appendProducts.value = true;
  pagination.isLoading = true;
  router.push({
    path: route.path,
    query: { ...route.query, page: page.value + 1 },
  });
};
const handleSetPage = (page: number) => {
  appendProducts.value = false;
  pagination.isLoading = true;
  router.push({
    path: route.path,
    query: { ...route.query, page },
  });
  scrollToElement(settingsRef.value); // мастхев фишка понимаш, баннеров напихали а теперь "ой чей-то товары в поле видимости не попадают" -_-
};

watch(
  data,
  (next) => {
    pagination = {
      ...(next?.pagination || {}),
      pageSize: PRODUCTS_LIMIT,
    };

    if (appendProducts.value) {
      products.value.push(...(next?.goods || []));
    } else {
      products.value = next?.goods || [];
    }
  },
  { immediate: true },
);

watch(pending, (v) => v && !appendProducts.value && window.scrollTo(0, 0));
watch(sortingModel, (v: ISortingValue) => {
  router.push({ query: { ...route.query, sortBy: v.value, order: v.order } });
});

onMounted(() => {
  send("PromoCatalog:List:View", { categoryName: currentCategory.value?.name });
});

useHead({
  link: [
    {
      rel: "canonical",
      href: route.path,
    },
  ],
});

const { metaTitle, metaDesc, metaKeywords } = useCatalogMeta(
  currentCategory.value?.name || SEO_DEFAULT_CATEGORY_TITLE,
);

useSeoMeta({
  title: metaTitle,
  ogTitle: metaTitle,
  description: metaDesc,
  ogDescription: metaDesc,
  keywords: metaKeywords,
});
</script>

<template>
  <div v-show="!pending" class="promo-catalog-page">
    <VBreadcrumbsWrapped :items="breadcrumbs" />

    <VWrapperLocal>
      <VPageBack text="Главная" :route="Routes.Main" @click="onBackClick" />
    </VWrapperLocal>

    <VCatalogHeader
      v-if="currentCategory"
      :title="currentCategory.name"
      :image="currentCategory.preview"
      :count="data?.pagination?.total || 0"
    />

    <VCatalogHeaderSlider
      :current-category-tree-name="
        currentCategory
          ? categoriesStore.getTreeNameByKey(currentCategory.key)
          : ''
      "
      promo
    />

    <VWrapperLocal>
      <VStack direction="row">
        <VStackItem :size="{ xl: 3 }">
          <VHidden :hide="{ xs: true, xl: false }">
            <div>
              <VCatalogFilters
                v-if="currentCategory"
                v-model="innerFilters"
                :categories-store="categoriesStore"
                :category="currentCategory"
                :count-change="countChangeFilters"
                :loading="pending"
                @reset="onResetFilters"
              />
            </div>
          </VHidden>

          <VCatalogFiltersModal
            v-if="currentCategory"
            v-model="innerFilters"
            :name="filtersModal.name"
            :is-open="filtersModal.isOpen.value"
            :is-active="filtersModal.isActive.value"
            :categories-store="categoriesStore"
            :category="currentCategory"
            :count-change="countChangeFilters"
            :store-code="requestShopCode"
            :default-filters="defaultFilters"
            :request-filters="requestFilters"
            :request-promotions="requestPromotions"
            :catalog-filter-functions="{ getFiltersPayload, getArrayFilters }"
            @close="filtersModal.close"
          />
        </VStackItem>

        <VStackItem :size="{ xs: 12, xl: 9 }">
          <div ref="settingsRef" class="promo-catalog-page__settings">
            <VCatalogFiltersButton
              :mark="!!countChangeFilters"
              @click="filtersModal.open()"
            />

            <VCatalogSorting
              v-model="sortingModel"
              :default-sort-value="DEFAULT_SORT_VALUE_PROMO"
              :config-desktop="promoSortingConfigDesktop"
              :config-mobile="promoSortingConfigMobile"
              :disabled="pending"
            />
          </div>

          <VStack
            direction="row"
            class="promo-catalog-page__products"
            align="stretch"
            :gap="{ xs: '8px', s: '12px', m: '20px', l: '24px' }"
          >
            <VCatalogListing
              class="promo-catalog-page__listing"
              :ya-slot-position="BANNER_POSITION_INDEX"
              :ya-slot-double-position="DOUBLE_BANNER_POSITION_INDEX"
              :items="products"
              :pagination="pagination"
              :loading="pending && !appendProducts"
              :empty-url-primary-button="Routes.Catalog"
              :empty-url-secondary-button="Routes.PromoCatalog"
              :item-size="{ xs: 6, m: 4, ml: 3 }"
              :analytics-sender="send"
              :analytics-payload="analyticsCatalogListingPayload"
              @pagination:show-more="handleShowMore"
              @pagination:set-page="handleSetPage"
            >
              <VYaSlot
                v-bind="yaSlotConfig"
                :api-ready="yaSlotSuccess"
                :api-error="yaSlotError"
                :analytics-sender="send"
                :analytics-view="yaSlotAnalyticsView"
                :analytics-click="yaSlotAnalyticsClick"
                has-product
                with-hover
              />
            </VCatalogListing>
          </VStack>

          <VCatalogBottomSlot />
        </VStackItem>
      </VStack>
    </VWrapperLocal>
  </div>
</template>

<style lang="postcss" scoped>
.promo-catalog-page {
  &__settings {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  &__pagination {
    margin-top: 60px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__products {
    position: relative;
    min-height: 140px;
    z-index: var(--pl-z-index-level1);
  }

  &__listing {
    flex: 1 1 auto;
  }
}
</style>
